<div class="table-responsive" id="table">

  <!-- Toolbar. Search is promoted out of the filter set because it is the control
       people reach for first; everything else sits behind the Filters toggle so the
       table is not pushed below the fold by a row of empty inputs. -->
  <div class="table-toolbar" *ngIf="filterItems?.length > 0 || allowExport">
    <div class="table-toolbar__search" *ngIf="searchFilter">
      <i class="fas fa-search table-toolbar__search-icon" aria-hidden="true"></i>
      <input
        type="text"
        class="table-toolbar__search-input"
        [attr.aria-label]="searchFilter.label"
        [placeholder]="searchFilter.label"
        [value]="searchFilter.value || ''"
        (input)="onFilterInputChange(searchFilter, $event.target.value)" />
    </div>

    <button
      type="button"
      class="table-toolbar__btn"
      *ngIf="panelFilters.length > 0"
      [attr.aria-expanded]="showFilterPanel"
      (click)="toggleFilterPanel()">
      <i class="fas fa-filter" aria-hidden="true"></i>
      Filters
      <span class="table-toolbar__count" *ngIf="hasActiveFilters">{{activeFilters.length}}</span>
    </button>

    <!-- Export sits next to the filters it acts on. It used to be a menu item in the page
         header, which is nowhere near the table it exports. -->
    <!-- The count is on the button on purpose: the export covers the rows loaded so far,
         which is page one until the user scrolls further. Saying so avoids the surprise
         of exporting 25 rows out of several thousand. -->
    <button
      type="button"
      class="table-toolbar__btn"
      *ngIf="allowExport"
      [disabled]="!displayedData.length"
      [title]="'Exports the ' + displayedData.length + ' rows loaded so far. Scroll down to load more.'"
      (click)="export()">
      <i class="fas fa-cloud-download-alt" aria-hidden="true"></i>
      Export {{displayedData.length}} loaded
    </button>

    <span class="table-toolbar__records" *ngIf="count > 0">{{count}} record{{count === 1 ? '' : 's'}}</span>
  </div>

  <!-- Active filters, as removable chips. Previously the only way to tell a filter was
       applied was to look at the inputs, and the only way to clear one was to reload. -->
  <div class="table-chips" *ngIf="hasActiveFilters">
    <span class="table-chip" *ngFor="let filterOption of activeFilters">
      <span class="table-chip__label">{{filterOption.label}}:</span>
      <span class="table-chip__value">{{chipValue(filterOption)}}</span>
      <button
        type="button"
        class="table-chip__remove"
        [attr.aria-label]="'Remove filter ' + filterOption.label"
        (click)="removeFilter(filterOption)">&times;</button>
    </span>
    <button type="button" class="table-chips__clear" (click)="clearFilter()">Clear all</button>
  </div>

  <form class="table-filter-panel" *ngIf="showFilterPanel && panelFilters.length > 0">
    <div class="d-flex pt-3 flex-wrap justify-content-around">
      <div *ngFor="let filterOption of panelFilters">
        <div *ngIf="filterOption.dropdown" class="fieldset">
          <label class="legend">{{filterOption.label}}</label>
          <!-- Bound to the filter, not left to the browser. Without a binding the select
               kept showing its old selection after Clear all or after removing its chip:
               the value was cleared in the data but nothing wrote it back to the control.
               The empty placeholder is now a real "All" choice, so a filter can also be
               cleared from the dropdown itself. -->
          <select [ngModel]="filterOption.value"
                  [name]="filterOption.label"
                  (ngModelChange)="setValueForDropdownFilter($event, filterOption.label)"
                  class="text-field filter-dropdown">
            <option [ngValue]="null">All</option>
            <option [ngStyle]="{'background-color': option.bgcolor ? option.bgcolor : ''}" *ngFor="let option of filterOption.dropdownOptions" [ngValue]="option.value">{{option.displaytext}}</option>
          </select>
        </div>
        <div *ngIf="filterOption.date">
          <div class="fieldset">
            <label class="legend" for="price">{{filterOption.label}}</label>
            <input (change)="onFilterInputChange()" type="datetime-local" [(ngModel)]="filterOption.value" [name]="filterOption.label" class="text-field filter-date">
          </div>
        </div>
        <div *ngIf="filterOption.checkbox">
          <div class="fieldset">
            <label class="legend">{{filterOption.label}}</label>
            <input (change)="onFilterInputChange()" type="checkbox" [(ngModel)]="filterOption.value" [name]="filterOption.label" class="text-field">
          </div>
        </div>
      </div>
    </div>
  </form>

  <table #tableElement class="table align-items-center table-flush table-hover" *ngIf="displayedData.length > 0 else empty">
    <thead class="thead-light">
      <tr>
        <th *ngIf="allowMultiSelect && operation?.length > 0"><input id="headselect" (click)="onCheckBoxClicked($event)" type="checkbox"></th>
        <th> S/N </th>
        <th
          *ngFor="let column of resolvedColumns"
          [class.handCursor]="isSortable(column)"
          [class.table-th--sorted]="sortBy === column.sortKey"
          [attr.aria-sort]="ariaSort(column)"
          (click)="toggleSort(column)">
          {{column.label}}
          <i *ngIf="isSortable(column)" class="ml-1 fas" [ngClass]="sortIcon(column)" aria-hidden="true"></i>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        *ngFor="let item of displayedData; let i = index"
        (click)="rowClick(item)" >
        <td *ngIf="allowMultiSelect"><input #selectrow type="checkbox" [id]="i" (click)="onCheckBoxClicked($event)"></td>
        <td>{{i + 1}}</td>
        <td *ngFor="let column of resolvedColumns">
          <!-- A progress column prints its own text and draws a bar beneath it, so the
               reader still gets "3 of 8" rather than a bare percentage. -->
          <ng-container *ngIf="column.type === 'progress'; else plainCell">
            <div class="cell-progress__label">{{ item[column.key] }}</div>
            <div class="cell-progress" role="progressbar"
                 [attr.aria-valuenow]="percentFor(item, column)" aria-valuemin="0" aria-valuemax="100">
              <span [style.width.%]="percentFor(item, column)"></span>
            </div>
          </ng-container>
          <ng-template #plainCell>
            <ng-container *ngIf="isValidDate(item[column.key]); else notValidDate">
              {{ item[column.key] | date: 'yyyy-MM-dd HH:mm:ss' }}
            </ng-container>
            <ng-template #notValidDate>
              {{ item[column.key] }}
            </ng-template>
          </ng-template>
        </td>
      </tr>
    </tbody>
  </table>

  <ng-template #empty>
    <div class="text-center my-3">{{noRecordText}}</div>
  </ng-template>
  <div class="text-center" *ngIf="this.count != this.displayedData.length && isLoading">
    loading...
  </div>

</div>
